import React, { useState, useEffect } from 'react';
import { Target, TrendingUp, Wallet, AlertCircle } from 'lucide-react';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const Investments = ({ userData }: { userData?: any }) => {
  const [algos, setAlgos] = useState<any[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    fetch('https://motilalalgo.com/api/profile/market/algos')
      .then(res => res.json())
      .then(data => setAlgos(data))
      .catch(err => console.error(err));
  }, []);

  if (!userData) return null;

  const currentBalance = parseFloat(userData.wallet_balance || 0);

  const handleInvest = (algo: any) => {
    Swal.fire({
      title: `Invest in ${algo.symbol}`,
      html: `
        <div style="text-align: left; margin-top: 10px; font-family: 'Urbanist', sans-serif;">
           <div style="background: #f8fafc; padding: 15px; border-radius: 16px; margin-bottom: 20px; border: 1px solid #f1f5f9;">
             <div style="display: flex; justify-content: space-between; margin-bottom: 8px;">
               <span style="font-size: 12px; color: #64748b; font-weight: bold; text-transform: uppercase;">Available Balance</span>
               <span style="font-size: 14px; color: #0f172a; font-weight: 900;">₹${currentBalance.toFixed(2)}</span>
             </div>
             <div style="display: flex; justify-content: space-between; border-top: 1px dashed #cbd5e1; padding-top: 8px;">
               <span style="font-size: 12px; color: #64748b; font-weight: bold; text-transform: uppercase;">Min Investment</span>
               <span style="font-size: 14px; color: #3b82f6; font-weight: 900;">₹${algo.min_investment}</span>
             </div>
           </div>
           <label style="font-size: 12px; font-weight: 900; color: #0f172a; text-transform: uppercase; letter-spacing: 1px;">Enter Amount (₹)</label>
           <input type="number" id="invest-amount" class="swal2-input" style="margin-top: 8px; width: 100%; max-width: 100%; font-weight: bold; border-radius: 14px; border: 2px solid #f1f5f9; box-shadow: none;" placeholder="e.g. ${algo.min_investment}">
        </div>
      `,
      showCancelButton: true,
      confirmButtonColor: '#0f172a',
      cancelButtonColor: '#f8fafc',
      confirmButtonText: 'Confirm Investment',
      cancelButtonText: '<span style="color: #64748b; font-weight: bold;">Cancel</span>',
      customClass: {
        popup: 'rounded-[30px]',
        title: 'font-black text-2xl text-[#0f172a]',
        confirmButton: 'rounded-[14px] font-bold px-6 py-3',
        cancelButton: 'rounded-[14px] font-bold px-6 py-3 border border-[#f1f5f9]'
      },
      preConfirm: () => {
        const amount = (document.getElementById('invest-amount') as HTMLInputElement).value;
        if (!amount || parseFloat(amount) <= 0) {
          Swal.showValidationMessage('Please enter a valid amount');
        }
        return parseFloat(amount);
      }
    }).then(async (result) => {
      if (result.isConfirmed) {
        const amount = result.value;

        if (amount > currentBalance) {
          Swal.fire({
            title: 'Insufficient Balance!', 
            text: 'Please add funds to your wallet to continue.', 
            icon: 'warning',
            confirmButtonColor: '#0f172a',
            customClass: { popup: 'rounded-[30px]', confirmButton: 'rounded-[14px] font-bold' }
          });
          return;
        }

        try {
          const res = await fetch(`https://motilalalgo.com/api/profile/${userData.id}/invest`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ algo_id: algo.id, amount })
          });
          const data = await res.json();

          if (data.success) {
            Swal.fire({
              title: 'Invested Successfully!', 
              text: data.message, 
              icon: 'success',
              confirmButtonColor: '#10b981',
              customClass: { popup: 'rounded-[30px]', confirmButton: 'rounded-[14px] font-bold' }
            }).then(() => {
               window.location.href = '/account/portfolio';
            });
          } else {
            Swal.fire('Error', data.error, 'error');
          }
        } catch (error) {
          Swal.fire('Error', 'Server Error', 'error');
        }
      }
    });
  };

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-10 font-[Urbanist]">
      <div className="flex flex-col md:flex-row justify-between md:items-end gap-6">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-[#0f172a]">Market Algos</h1>
          <p className="text-[#64748b] font-medium mt-1 text-sm md:text-base">Invest in premium algorithmic trading setups.</p>
        </div>
        <div className="bg-white border border-[#f1f5f9] px-6 py-4 rounded-[24px] flex items-center gap-4 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="p-3 bg-[#eff6ff] rounded-[16px]"><Wallet className="text-[#3b82f6]" size={28} strokeWidth={2.5} /></div>
          <div>
            <p className="text-[11px] text-[#94a3b8] uppercase font-bold tracking-widest mb-0.5">Available Funds</p>
            <p className="text-2xl font-black text-[#0f172a] leading-tight">₹{currentBalance.toFixed(2)}</p>
          </div>
        </div>
      </div>

      {algos.length === 0 ? (
        <div className="bg-white border border-[#f1f5f9] rounded-[30px] p-16 text-center text-[#64748b] shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col items-center">
          <div className="w-20 h-20 bg-[#f8fafc] rounded-full flex items-center justify-center mb-6 border border-[#f1f5f9]">
            <AlertCircle size={32} className="text-[#cbd5e1]" strokeWidth={2.5} />
          </div>
          <h3 className="text-2xl font-black text-[#0f172a] mb-2">No Active Algos Found</h3>
          <p className="font-medium text-[#64748b]">The market is currently closed or no algos are active right now.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {algos.map((algo) => (
            <div key={algo.id} className="bg-white border border-[#f1f5f9] rounded-[24px] md:rounded-[30px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_15px_40px_rgb(59,130,246,0.08)] hover:-translate-y-1 hover:border-[#bfdbfe] transition-all duration-300 group flex flex-col">
              
              <div className="flex justify-between items-start mb-6">
                <div className="w-14 h-14 bg-[#f8fafc] rounded-[18px] flex items-center justify-center border border-[#f1f5f9] group-hover:bg-[#3b82f6] group-hover:border-[#3b82f6] transition-colors shadow-sm">
                  <TrendingUp size={24} strokeWidth={2.5} className={algo.symbol ? "text-[#3b82f6] group-hover:text-white transition-colors" : ""} />
                </div>
                <span className="bg-emerald-50 text-emerald-600 text-[10px] font-black px-3 py-1.5 rounded-full uppercase tracking-widest border border-emerald-100 shadow-sm">Active</span>
              </div>
              
              <div className="flex-grow">
                <h3 className="text-2xl font-black text-[#0f172a] mb-1">{algo.symbol}</h3>
                <p className="text-sm text-[#64748b] font-bold mb-6">{algo.stock_name}</p>
              </div>
              
              <div className="bg-[#f8fafc] rounded-[20px] p-5 mb-6 border border-[#f1f5f9] group-hover:bg-[#eff6ff]/70 transition-colors">
                <p className="text-[11px] text-[#94a3b8] uppercase tracking-widest font-bold mb-1.5">Min Investment</p>
                <p className="text-xl font-black text-[#0f172a]">₹{parseFloat(algo.min_investment).toFixed(2)}</p>
              </div>

              <button 
                onClick={() => handleInvest(algo)}
                className="w-full py-4 bg-[#0f172a] hover:bg-[#3b82f6] text-white font-black rounded-[16px] transition-all duration-300 active:scale-95 shadow-md hover:shadow-[0_8px_20px_rgb(59,130,246,0.3)] flex justify-center items-center gap-2 uppercase tracking-widest text-sm"
              >
                <Target size={18} strokeWidth={2.5} /> Invest Now
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Investments;